import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { parseProjectContextSearchParams } from '@gonfalon/app-context/parseProjectContextSearchParams';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { isTrial } from '@gonfalon/billing/subscriptions';
import { enableSdkSetupDrawer, installSdkVisibility } from '@gonfalon/dogfood-flags/dogfood-flags';
import { toHref, toSdkSetup } from '@gonfalon/navigator/navigators';
import { useOnboardingStorage } from '@gonfalon/onboarding/storage';
import type { OnboardingPath } from '@gonfalon/onboarding/types';
import { getEvaluationsUsageQuery } from '@gonfalon/rest-api/queries/flags';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { SdkSetupDrawer } from '@gonfalon/sdk-setup/components/SdkSetupDrawer/SdkSetupDrawer';
import { useSdkActivePolling } from '@gonfalon/sdk-setup/hooks';
import { LinkButton, Text } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';
import cx from 'clsx';
import { endOfDay, subDays } from 'date-fns';

import { trackSetUpSDKButtonClicked } from './analytics';

import styles from './CheckSdkConnection.module.css';
export const MAX_RETRY_ATTEMPTS = 3;

const today = new Date();
const oneWeekAgo = subDays(new Date(today), 7);
const endOfToday = endOfDay(today);

type CheckSdkConnectionProps = {
  connectActionText?: string;
  className?: string;
  component?: string;
  onboardingPath?: OnboardingPath;
  flagKey?: string;
  spacing?: 'default' | 'section';
  /** When provided, overrides the default "Set up SDK" click behaviour (navigate or open generic drawer). */
  onSdkSetupClick?: () => void;
};

export const CheckSdkConnection = ({
  connectActionText,
  className,
  component,
  flagKey,
  onboardingPath = 'flags',
  spacing = 'default',
  onSdkSetupClick,
}: CheckSdkConnectionProps) => {
  const projectKey = useProjectKey();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const environmentKey = parseProjectContextSearchParams(searchParams).selectedEnvironmentKey;

  const { data: evaluationsUsageData, isLoading: isEvaluationsUsageLoading } = useQuery({
    ...getEvaluationsUsageQuery({
      projectKey,
      featureFlagKey: flagKey || '',
      environmentKey: environmentKey || '',
      query: {
        from: oneWeekAgo.getTime().toString(),
        to: endOfToday.getTime().toString(),
      },
    }),
    enabled: !!(flagKey && environmentKey),
  });

  const navigate = useNavigate();
  const { setOnboardingStorage } = useOnboardingStorage();
  const { subscription } = useAccountContext();
  const isSdkSetupDrawerEnabled = enableSdkSetupDrawer();
  const [isSdkSetupDrawerOpen, setIsSdkSetupDrawerOpen] = useState(false);

  const shouldEnableQuery = installSdkVisibility() && isTrial(subscription);

  const { isActive, isLoading } = useSdkActivePolling(undefined, {
    enabled: shouldEnableQuery,
    maxAttempts: MAX_RETRY_ATTEMPTS,
  });

  const handleSetUpSDKClick = async () => {
    trackSetUpSDKButtonClicked(component);

    if (onSdkSetupClick) {
      onSdkSetupClick();
      return;
    }

    setOnboardingStorage({ path: onboardingPath });

    if (isSdkSetupDrawerEnabled) {
      setIsSdkSetupDrawerOpen(true);
    } else {
      await navigate(toHref(toSdkSetup({ projectKey })));
    }
  };

  const isLoadingFlagEvaluations = flagKey && isEvaluationsUsageLoading;
  const flagHasEvaluations = flagKey && evaluationsUsageData && evaluationsUsageData.totalEvaluations !== 0;

  if (!shouldEnableQuery || isActive || isLoading || flagHasEvaluations || isLoadingFlagEvaluations) {
    return null;
  }

  return (
    <>
      <div className={cx(styles.container, spacing === 'section' && styles.sectionSpacing, className)}>
        <div className={styles.content}>
          <Icon name="circle-dashed" />
          <div className={styles.text}>
            <Text className={styles.title}>SDK not connected</Text>
            <Text className={styles.description}>{connectActionText ?? 'Connect your SDK'}</Text>
          </div>
        </div>
        <div className={styles.ctas}>
          <LinkButton variant="primary" onPress={handleSetUpSDKClick}>
            Set up SDK
          </LinkButton>
        </div>
      </div>
      {isSdkSetupDrawerEnabled && (
        <SdkSetupDrawer isOpen={isSdkSetupDrawerOpen} onOpenChange={setIsSdkSetupDrawerOpen} />
      )}
    </>
  );
};
